import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Controller } from 'react-hook-form';

import Input from '../../../common/form/Input';
import MaskInput from '../../../common/form/MaskInput';
import Button from '../../../common/buttons/Button';
import RadioButtons from '../../../common/buttons/RadioButtons';
import Checkbox from '../../../common/form/Checkbox';
import { FormValues } from './types';

interface WorkLogisticsStepProps {
	formValues: FormValues;
	setValue: any;
	control: any;
	errors: any;
	isRequired?: boolean;
}

export default function WorkLogisticsStep({
	formValues,
	setValue,
	control,
	errors,
	isRequired = true,
}: WorkLogisticsStepProps) {
	return (
		<Box sx={{ py: 2 }}>
			{/* Local Work Location */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Local Work Location
			</Typography>

			<Box sx={{ mb: 3 }}>
				<Controller
					name="localWorkLocations"
					control={control}
					rules={{
						validate: (value) => {
							const filledLocations =
								value?.filter((loc) => loc?.trim()) || [];
							return (
								filledLocations.length > 0 ||
								'At least one working city is required'
							);
						},
					}}
					render={({ field, fieldState }) => (
						<>
							<Typography variant="body2" sx={{ mb: 1 }}>
								Primary working city (ex. Portland, OR)
								{isRequired && (
									<span style={{ color: '#d32f2f' }}> *</span>
								)}
							</Typography>
							{field.value.map((location, index) => (
								<Box
									key={index}
									sx={{
										display: 'flex',
										gap: 2,
										alignItems: 'center',
										mb: 2,
									}}
								>
									<Input
										value={location}
										onChange={(e) => {
											const newLocations = [
												...field.value,
											];
											newLocations[index] =
												e.target.value;
											field.onChange(newLocations);
										}}
										fullWidth
										placeholder="Enter city and state"
										error={!!fieldState.error}
									/>
									{field.value.length > 1 && (
										<Button
											variant="text"
											color="error"
											onClick={() => {
												const newLocations =
													field.value.filter(
														(_, i) => i !== index
													);
												field.onChange(newLocations);
											}}
										>
											Remove
										</Button>
									)}
								</Box>
							))}
							{fieldState.error && (
								<Typography
									variant="caption"
									color="error"
									sx={{ mt: 1 }}
								>
									{fieldState.error.message}
								</Typography>
							)}
							<Button
								variant="text"
								color="primary"
								onClick={() => {
									field.onChange([...field.value, '']);
								}}
								sx={{ alignSelf: 'flex-start' }}
							>
								+ ADD ADDITIONAL LOCAL WORK LOCATION
							</Button>
						</>
					)}
				/>
			</Box>

			{/* Typical Employment Type */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Typical Employment Type
			</Typography>

			<Box sx={{ mb: 3 }}>
				<Controller
					name="employmentTypePreference"
					control={control}
					rules={
						isRequired
							? {
									required:
										'Preferred tax / employment type is required',
							  }
							: {}
					}
					render={({ field, fieldState }) => (
						<RadioButtons
							label="Preferred tax / employment type"
							name="employmentTypePreference"
							options={[
								{
									value: 'payroll_employee',
									label: 'Payroll Employee (inclusive of fringes)',
								},
								{
									value: 'vendor_contractor',
									label: 'Vendor/Contractor (w-9)',
								},
								{
									value: 'loan_out',
									label: 'Loan-out',
								},
								{
									value: 'no_preference',
									label: 'No preference',
								},
							]}
							value={field.value}
							onChange={field.onChange}
							required={isRequired}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
						/>
					)}
				/>
			</Box>

			{formValues.employmentTypePreference === 'loan_out' && (
				<Box sx={{ mb: 4 }}>
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Controller
								name="loanOutCompanyName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Loan-out company name (if applicable)"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Controller
								name="loanOutCompanyContactName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Loan-out company contact name"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<MaskInput
								id="loanOutCompanyPhone"
								name="loanOutCompanyPhone"
								label="Loan-out company #"
								value={formValues.loanOutCompanyPhone}
								onChange={(e) =>
									setValue(
										'loanOutCompanyPhone',
										e.target.value,
										{ shouldDirty: true }
									)
								}
								mask="phone"
								fullWidth
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Controller
								name="loanOutCompanyEmail"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Loan-out company email"
										fullWidth
									/>
								)}
							/>
						</Grid>
					</Grid>
				</Box>
			)}

			{/* Payment */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Payment
			</Typography>

			<Box sx={{ mb: 4 }}>
				<Typography variant="body2" sx={{ mb: 2 }}>
					Preferred payment method
				</Typography>
				<Controller
					name="preferredPaymentMethods"
					control={control}
					render={({ field }) => (
						<Checkbox
							items={[
								{
									id: 'direct_deposit',
									name: 'Direct Deposit / ACH (best practice but please note additional preferred methods as needed)',
									checked:
										field.value.includes('direct_deposit'),
									handleOnChange: (event) => {
										const checked = event.target.checked;
										const methods = field.value;
										if (checked) {
											field.onChange([
												...methods,
												'direct_deposit',
											]);
										} else {
											field.onChange(
												methods.filter(
													(m) =>
														m !== 'direct_deposit'
												)
											);
										}
									},
								},
								{
									id: 'check',
									name: 'Check',
									checked: field.value.includes('check'),
									handleOnChange: (event) => {
										const checked = event.target.checked;
										const methods = field.value;
										if (checked) {
											field.onChange([
												...methods,
												'check',
											]);
										} else {
											field.onChange(
												methods.filter(
													(m) => m !== 'check'
												)
											);
										}
									},
								},
								{
									id: 'zelle_venmo',
									name: 'Zelle / Venmo',
									checked:
										field.value.includes('zelle_venmo'),
									handleOnChange: (event) => {
										const checked = event.target.checked;
										const methods = field.value;
										if (checked) {
											field.onChange([
												...methods,
												'zelle_venmo',
											]);
										} else {
											field.onChange(
												methods.filter(
													(m) => m !== 'zelle_venmo'
												)
											);
										}
									},
								},
								{
									id: 'bitcoin',
									name: 'Bitcoin',
									checked: field.value.includes('bitcoin'),
									handleOnChange: (event) => {
										const checked = event.target.checked;
										const methods = field.value;
										if (checked) {
											field.onChange([
												...methods,
												'bitcoin',
											]);
										} else {
											field.onChange(
												methods.filter(
													(m) => m !== 'bitcoin'
												)
											);
										}
									},
								},
								{
									id: 'no_preference',
									name: 'No preference',
									checked:
										field.value.includes('no_preference'),
									handleOnChange: (event) => {
										const checked = event.target.checked;
										const methods = field.value;
										if (checked) {
											field.onChange([
												...methods,
												'no_preference',
											]);
										} else {
											field.onChange(
												methods.filter(
													(m) => m !== 'no_preference'
												)
											);
										}
									},
								},
							]}
						/>
					)}
				/>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography
					variant="body2"
					sx={{ color: 'text.secondary', mb: 1 }}
				>
					Information about Orbito's privacy policy and information
					sharing can be found below.
				</Typography>
				<Button
					variant="text"
					color="primary"
					onClick={() => {
						// TODO: Implement privacy policy modal or navigation
						console.log('View privacy policy clicked');
					}}
					sx={{ p: 0, textDecoration: 'underline' }}
				>
					VIEW PRIVACY POLICY
				</Button>
			</Box>
		</Box>
	);
}
